import * as Sentry from '@sentry/browser'
import { flowRight as compose } from 'lodash'

import React, { useState, useEffect } from 'react'
import { RouteChildrenProps } from 'react-router-dom'

import { useTranslation } from 'react-i18next'
import { useSetRecoilState, useRecoilValue } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { ModalChainTicketDetail } from 'components/Modal'
import { Button } from 'components'

import { TICKET_ACTION } from 'helpers/constants'

import {
  IResponseTicket,
  ISchemaFormAssignTicket,
  ISchemaFormTransferringTicket,
  // Features
  TicketAssignModal,
  TicketInfo,
  TicketCustomerInfo,
  TicketProductInfo,
  TicketProductHistoryModal,
  TicketProgressInfo,
  TicketRequestCloseModal,
  TicketPendingModal,
  TicketTransferModal,
  closeTicket,
  pendingTicket,
  postAssignTicket,
  formatPendingTicket,
  formatPostTaskAssign,
  formatTransferringTicket,
  transferTicket,
  // Constants
  COLUMNS_PROGRESS_INFO,
  ISchemaFormCloseTicketPayload,
  initialChainingModal,
  initialPayloadForCloseTask,
  ISchemaFormPendingTicket,
  initialFormPendingTicket,
  renderBreadcrumbsDetail,
  initProducts,
  uploadPhotoBeforeTask,
  uploadPhotoAfterTask,
  findTicketById,
} from 'features/Ticket'

import { withBackOfficeCms } from 'helpers/hocs'
import { useBreadcrumbs } from 'helpers/hooks'
import { STATUSPROGRESS } from 'helpers/constants'
import { currentStatusProgressState, userProfileState } from 'recoils/atoms'

import type { Product } from 'features/Ticket/TicketProductInfo'
import { resizeMultipleImage } from 'helpers/resizeMultipleImage'

type IPropsTicketDetail = RouteChildrenProps<{ id: string }>

const TicketDetail: React.FC<IPropsTicketDetail> = (props) => {
  const base = 'con-ticket__detail'
  const { history, match } = props

  const { t } = useTranslation(['layout', 'ticket', 'common'])

  const {
    REQUEST_CLOSE_TICKET,
    TICKET_ASSIGN,
    TICKET_TRANSFER,
    PENDING_TICKET,
  } = TICKET_ACTION

  const [ticketId, setTicketId] = useState<string>('')

  const [openProgress, setOpenProgress] = useState<boolean>(true)
  const [openHistoryModal, setOpenHistoryModal] = useState<boolean>(false)

  const [openAssignTicket, setOpenAssignTicket] = useState<boolean>(false)
  const [openRequestCloseTicket, setOpenRequestCloseTicket] =
    useState<boolean>(false)
  const [openPendingTicket, setOpenPendingTicket] = useState<boolean>(false)
  const [openTransferTicket, setOpenTransferTicket] = useState<boolean>(false)

  const [productId, setProductId] = useState<string>('')
  const [productIds, setProductIds] = useState<string[]>([])
  const [products, setProducts] = useState<Product[]>(initProducts)

  const [ticketDetail, setTicketDetail] = useState<IResponseTicket>()

  const setIsOpenModal = useSetRecoilState(openModalState)
  const statusProgress = useRecoilValue(currentStatusProgressState)

  const [isStatusPending, setIsStatusPending] = useState<boolean>(false)
  const [isStatusTransfer, setIsStatusTransfer] = useState<boolean>(false)
  const [isStatusClosed, setIsStatusClosed] = useState<boolean>(false)

  const [isChainingModal, setIsChainingModal] = useState(initialChainingModal)
  const [typeModalChaining, setTypeModalChaining] = useState('')

  const [payloadForCloseTask, setPayloadForCloseTask] =
    useState<ISchemaFormCloseTicketPayload>(initialPayloadForCloseTask)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [payloadPendingTask, setPayloadPendingTask] =
    useState<ISchemaFormPendingTicket>(initialFormPendingTicket)

  const [payloadAssignTicket, setPayloadAssignTicket] = useState<
    ISchemaFormAssignTicket | undefined | null
  >()

  const [payloadTicketTransfer, setPayloadTicketTransfer] = useState<
    ISchemaFormTransferringTicket | undefined | null
  >()

  useEffect(() => {
    setIsStatusPending(true)

    if (match?.params) {
      setTicketId(match.params.id)
    }
  }, [match?.params])

  useEffect(() => {
    if (isChainingModal.openSuccess) {
      handleOpenSuccess()
    }
  }, [isChainingModal.openSuccess])

  useEffect(() => {
    const _isStatusPending =
      statusProgress.toLowerCase() === STATUSPROGRESS.PENDING
    const _isStatusTransfer =
      statusProgress.toLowerCase() === STATUSPROGRESS.TRANSFERRING
    const _isStatusClosed =
      statusProgress.toLowerCase() === STATUSPROGRESS.CLOSED

    _isStatusPending ? setIsStatusPending(true) : setIsStatusPending(false)

    _isStatusTransfer && setIsStatusTransfer(true)

    _isStatusClosed && setIsStatusClosed(true)
  }, [statusProgress])

  useBreadcrumbs(renderBreadcrumbsDetail(ticketId))

  useEffect(() => {
    const fetchTicketDetail = async (id: string) => {
      if (id && openAssignTicket) {
        const { data } = await findTicketById(id)
        setTicketDetail(data)
      }
    }

    fetchTicketDetail(ticketId)
  }, [openAssignTicket])

  const handleOpenHistoryModal = async (id: string) => {
    setOpenHistoryModal(!openHistoryModal)
    setProductId(id)
  }

  const onRefreshDetail = () => history.go(0)

  const { profileId: userId, profileFullName: userName } =
    useRecoilValue(userProfileState)

  const callApiForCloseTask = async () => {
    if (typeModalChaining === REQUEST_CLOSE_TICKET) {
      try {
        setIsLoading(true)

        const hasCompletedPhoto = payloadForCloseTask.completedPhoto.length > 0
        if (ticketDetail && hasCompletedPhoto) {
          const resizedPhoto = await resizeMultipleImage(
            payloadForCloseTask.completedPhoto,
          )
          await uploadPhotoAfterTask(ticketDetail.taskAssignId, resizedPhoto)
        }

        await closeTicket(ticketId, payloadForCloseTask, handleOpenSuccess)

        setIsChainingModal((prevState) => ({
          ...prevState,
          openSuccess: false,
        }))

        setIsLoading(false)
        setPayloadForCloseTask(initialPayloadForCloseTask)
      } catch (error) {
        setIsLoading(false)
        Sentry.captureException(error)
      }
    }

    if (typeModalChaining === PENDING_TICKET) {
      try {
        setIsLoading(true)
        const payload = formatPendingTicket(
          payloadPendingTask,
          userId,
          userName,
        )

        const hasPendingPhoto = payloadPendingTask.pendingPhoto.length > 0
        if (ticketDetail && hasPendingPhoto) {
          const resizedPhoto = await resizeMultipleImage(
            payloadPendingTask.pendingPhoto,
          )
          await uploadPhotoBeforeTask(ticketDetail.taskAssignId, resizedPhoto)
        }
        await pendingTicket(ticketId, payload, handleOpenSuccess)

        setIsChainingModal((prevState) => ({
          ...prevState,
          openSuccess: false,
        }))

        setIsLoading(false)
        setPayloadPendingTask(initialFormPendingTicket)
      } catch (error) {
        setIsLoading(false)
        Sentry.captureException(error)
      }
    }

    if (typeModalChaining === TICKET_ASSIGN) {
      try {
        setIsLoading(true)
        const payload = formatPostTaskAssign(
          ticketId,
          payloadAssignTicket as ISchemaFormAssignTicket,
        )

        await postAssignTicket(payload, handleOpenSuccess)

        setIsChainingModal((prevState) => ({
          ...prevState,
          openSuccess: false,
        }))

        setIsLoading(false)
        setPayloadAssignTicket(null)
      } catch (error) {
        setIsLoading(false)
        Sentry.captureException(error)
      }
    }

    if (typeModalChaining === TICKET_TRANSFER) {
      try {
        setIsLoading(true)

        const payload = formatTransferringTicket(
          ticketId,
          userId || '',
          userName || '',
          payloadTicketTransfer as ISchemaFormTransferringTicket,
          ticketDetail?.transferringAt || '',
        )

        await transferTicket(payload, handleOpenSuccess)

        setIsChainingModal((prevState) => ({
          ...prevState,
          openSuccess: false,
        }))

        setIsLoading(false)
        setPayloadTicketTransfer(null)
      } catch (error) {
        setIsLoading(false)
        Sentry.captureException(error)
      }
    }
  }

  const handleCloseModalAll = () => {
    setOpenRequestCloseTicket(false)
    setOpenAssignTicket(false)
    setOpenPendingTicket(false)
    setOpenTransferTicket(false)
  }

  const handleOpenModalAll = () => {
    if (typeModalChaining === REQUEST_CLOSE_TICKET) {
      setOpenRequestCloseTicket(true)
    }

    if (typeModalChaining === TICKET_ASSIGN) {
      setOpenAssignTicket(true)
    }

    if (typeModalChaining === PENDING_TICKET) {
      setOpenPendingTicket(true)
    }

    if (typeModalChaining === TICKET_TRANSFER) {
      setOpenTransferTicket(true)
    }
  }

  // handle success modal
  const handleOpenSuccess = () => {
    handleCloseModalAll()

    setIsChainingModal((prevState) => ({
      ...prevState,
      openConfirm: false,
      openSuccess: true,
    }))

    setTimeout(() => {
      onRefreshDetail()
    }, 1000)
  }

  const handleCloseSuccessModal = () => {
    setIsChainingModal((prevState) => ({
      ...prevState,
      openSuccess: false,
    }))
  }

  // handle confirm modal
  const handleCloseConfirmModal = () => {
    handleOpenModalAll()

    setIsChainingModal((prevState) => ({
      ...prevState,
      openConfirm: false,
    }))
  }

  // handle cancel modal
  const handleRejectCloseModal = () => {
    setIsOpenModal(false)
    setPayloadForCloseTask(initialPayloadForCloseTask)
    setPayloadPendingTask(initialFormPendingTicket)
    setPayloadAssignTicket(null)
    setPayloadTicketTransfer(null)

    setIsChainingModal((prevState) => ({
      ...prevState,
      openCancel: false,
    }))
  }

  const handleSubmitCloseModal = () => {
    handleOpenModalAll()

    setIsChainingModal((prevState) => ({
      ...prevState,
      openCancel: false,
    }))
  }

  // handle request close modal
  const handleSubmitRequestCloseTaskModal = () => {
    handleCloseModalAll()

    setIsChainingModal((prevState) => ({
      ...prevState,
      openConfirm: true,
    }))
  }

  const handleOpenConfirmCloseModal = (
    value?: ISchemaFormAssignTicket | ISchemaFormTransferringTicket,
  ) => {
    handleCloseModalAll()
    if (value && typeModalChaining === TICKET_ASSIGN) {
      setPayloadAssignTicket(value as ISchemaFormAssignTicket)
    }

    if (value && typeModalChaining === TICKET_TRANSFER) {
      setPayloadTicketTransfer(value as ISchemaFormTransferringTicket)
    }

    setIsChainingModal((prevState) => ({
      ...prevState,
      openCancel: true,
    }))
  }

  const handleOnClickAssignTasks = () => {
    if (isStatusTransfer) {
      setOpenTransferTicket(true)
      setTypeModalChaining(TICKET_TRANSFER)
    } else {
      setOpenAssignTicket(true)
      setTypeModalChaining(TICKET_ASSIGN)
    }
  }

  const disableDefault = !statusProgress || isStatusClosed
  const isDisablePending = disableDefault || isStatusPending
  const isDisableRequestClose = disableDefault
  const isDisableAssign = disableDefault

  const tasksButtonElement = (
    <div className={`${base}__button--wrap`}>
      <div className={`${base}__button-group`}>
        <div className={`${base}__button`}>
          <Button
            as='link'
            disabled={isDisablePending}
            onClick={() => {
              setTypeModalChaining(PENDING_TICKET)
              setOpenPendingTicket(true)
            }}
          >
            {t('ticket:detailPendingTicket.button')}
          </Button>
          <Button
            variants='secondary'
            className={
              isDisableRequestClose
                ? `${base}__button-request-close-ticket-tasks--disable`
                : `${base}__button-request-close-ticket-tasks`
            }
            disabled={isDisableRequestClose}
            onClick={() => {
              setTypeModalChaining(REQUEST_CLOSE_TICKET)
              setOpenRequestCloseTicket(true)
            }}
          >
            {t('ticket:requestCloseTicket.button')}
          </Button>
          <Button
            className={`${base}__button--assign-tasks`}
            disabled={isDisableAssign}
            onClick={() => handleOnClickAssignTasks()}
          >
            {t('ticket:assignTicket.button')}
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className={base}>
      <div className={`${base}__ticket`}>
        <TicketInfo
          ticketId={ticketId}
          setProductIds={setProductIds}
          setTicketDetail={setTicketDetail}
        />
        <TicketCustomerInfo
          ticketDetail={ticketDetail}
          branchId={ticketDetail?.branchId}
        />
      </div>

      <div className={`${base}__products`}>
        <TicketProductInfo
          ticketDetail={ticketDetail}
          productIds={productIds}
          products={products}
          onOpenHistoryModal={(id) => handleOpenHistoryModal(id)}
          setProducts={setProducts}
        />
      </div>

      <div className={`${base}__progress-logs`}>
        <TicketProgressInfo
          columns={COLUMNS_PROGRESS_INFO}
          ticketId={ticketId}
          openProgress={openProgress}
          products={products}
          onOpenProgress={() => setOpenProgress(!openProgress)}
          tasksButtonElement={tasksButtonElement}
        />
      </div>

      {openHistoryModal && productId && (
        <TicketProductHistoryModal
          productId={productId}
          isOpen={openHistoryModal}
          onClose={() => setOpenHistoryModal(false)}
        />
      )}

      {openPendingTicket && (
        <TicketPendingModal
          isOpen={true}
          onClose={handleOpenConfirmCloseModal}
          setDisabledPendingButton={setIsStatusPending}
          setConfirmModal={handleSubmitRequestCloseTaskModal}
          payloadBeforeSubmit={payloadPendingTask}
          setPayloadBeforeSubmit={setPayloadPendingTask}
          setOpenPendingTicket={() => setOpenPendingTicket(false)}
        />
      )}

      {openRequestCloseTicket && (
        <TicketRequestCloseModal
          id={ticketId}
          isOpen={openRequestCloseTicket}
          onClose={handleOpenConfirmCloseModal}
          onRefreshDetail={onRefreshDetail}
          setConfirmModal={handleSubmitRequestCloseTaskModal}
          payloadForCloseTask={payloadForCloseTask}
          setPayloadForCloseTask={setPayloadForCloseTask}
          setOpenRequestCloseTicket={() => setOpenRequestCloseTicket(false)}
        />
      )}

      {openAssignTicket && ticketDetail && (
        <TicketAssignModal
          id={ticketId}
          isOpen={true}
          ticket={ticketDetail}
          onClose={handleOpenConfirmCloseModal}
          onRefreshDetail={onRefreshDetail}
          setConfirmModal={handleSubmitRequestCloseTaskModal}
          payloadBeforeSubmit={payloadAssignTicket}
          setPayloadBeforeSubmit={setPayloadAssignTicket}
          setOpenAssignTicket={() => setOpenAssignTicket(false)}
        />
      )}

      {openTransferTicket && ticketDetail && (
        <TicketTransferModal
          id={ticketId}
          isOpen={true}
          ticket={ticketDetail}
          onClose={handleOpenConfirmCloseModal}
          setConfirmModal={handleSubmitRequestCloseTaskModal}
          onRefreshDetail={onRefreshDetail}
          payloadBeforeSubmit={payloadTicketTransfer}
          setPayloadBeforeSubmit={setPayloadTicketTransfer}
          setOpenTransferTicket={() => setOpenTransferTicket(false)}
        />
      )}

      <ModalChainTicketDetail
        type={typeModalChaining}
        isLoading={isLoading}
        isChainingModal={isChainingModal}
        callApiForCloseTask={callApiForCloseTask}
        handleCloseConfirmModal={handleCloseConfirmModal}
        handleRejectCloseModal={handleRejectCloseModal}
        handleSubmitCloseModal={handleSubmitCloseModal}
        handleCloseSuccessModal={handleCloseSuccessModal}
      />
    </div>
  )
}

export default compose(withBackOfficeCms)(TicketDetail)
